import * as React from "react"
import { Copy, Download, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useBookingT } from "@/hooks/use-translations"
import { fetchEmployeeNotice } from "@/features/integrations/hris/hrisApi"

type Props = {
  connectionId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

const LANGUAGE_LABELS: Record<string, string> = { en: "English", fr: "Français", de: "Deutsch", es: "Español" }

/**
 * The notice employees must get before the first sync (Schedule E §4.2(b)).
 * The customer sends it themselves; the bracketed parts are theirs to fill.
 */
export function HrisEmployeeNoticeDialog({ connectionId, open, onOpenChange }: Props) {
  const t = useBookingT()
  const [language, setLanguage] = React.useState("en")
  const [notice, setNotice] = React.useState<{ languages: string[]; text: string } | null>(null)

  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    setNotice(null)
    fetchEmployeeNotice(connectionId, language)
      .then((n) => !cancelled && setNotice(n))
      .catch((e: Error) => !cancelled && toast.error(e.message))
    return () => {
      cancelled = true
    }
  }, [open, connectionId, language])

  const copy = async () => {
    if (!notice) return
    await navigator.clipboard.writeText(notice.text)
    toast.success(t("hris_notice_copied", "Copied. Fill in the parts in brackets before sending."))
  }

  const download = () => {
    if (!notice) return
    const url = URL.createObjectURL(new Blob([notice.text], { type: "text/plain;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = `employee-notice-${language}.txt`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader className="text-left">
          <DialogTitle>{t("hris_notice_title", "Notice to your employees")}</DialogTitle>
          <DialogDescription className="text-neutral-800">
            {t(
              "hris_notice_hint",
              "Send this to your employees before the first sync: the law requires telling them what is shared, why and with whom. Fill in the parts in brackets. Where you have a works council, consult it first.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={language} onValueChange={setLanguage}>
          <TabsList>
            {(notice?.languages ?? Object.keys(LANGUAGE_LABELS)).map((code) => (
              <TabsTrigger key={code} value={code}>
                {LANGUAGE_LABELS[code] ?? code}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-4">
          {notice ? (
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-neutral-900">{notice.text}</pre>
          ) : (
            <Loader2 className="size-4 animate-spin text-brand-500" />
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={!notice} onClick={download}>
            <Download className="size-4" />
            {t("hris_notice_download", "Download")}
          </Button>
          <Button disabled={!notice} onClick={() => void copy()}>
            <Copy className="size-4" />
            {t("hris_notice_copy", "Copy text")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
